import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';

export default function PrestadorContratosGrid({ contratos, loading, onSelectContrato }) {
  const [filtro, setFiltro] = useState('');

  const filtrados = useMemo(() => {
    if (!contratos?.length) return [];
    const t = filtro.trim().toLowerCase();
    if (!t) return contratos;
    return contratos.filter((c) => {
      const n = (c.numeroContrato || '').toLowerCase();
      const e = (c.empresa || '').toLowerCase();
      const notas = (c.notasNumeros || '').toLowerCase();
      return n.includes(t) || e.includes(t) || notas.includes(t);
    });
  }, [contratos, filtro]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (!contratos?.length) {
    return (
      <Typography color="text.secondary" sx={{ py: 3 }}>
        Nenhum contrato ativo vinculado ao seu cadastro. O vínculo é feito pelo CNPJ ou pelo nome da empresa no SGC
        (ex.: contratos cadastrados como &quot;IDMED&quot;).
      </Typography>
    );
  }

  return (
    <Box>
      <TextField
        size="small"
        fullWidth
        placeholder="Buscar por número, empresa ou nota fiscal..."
        value={filtro}
        onChange={(e) => setFiltro(e.target.value)}
        sx={{ mb: 2, maxWidth: 480 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          )
        }}
      />

      {filtrados.length === 0 ? (
        <Typography color="text.secondary">Nenhum contrato com o filtro informado.</Typography>
      ) : (
        <Grid container spacing={2}>
          {filtrados.map((contrato) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={contrato.id || contrato.numeroContrato}>
              <Card
                sx={{
                  height: '100%',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 4px 20px rgba(25,118,210,0.2)',
                    borderColor: '#1976d2'
                  }
                }}
              >
                <CardActionArea onClick={() => onSelectContrato(contrato)} sx={{ height: '100%', p: 2 }}>
                  <CardContent sx={{ p: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
                      <BusinessIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700} color="primary">
                          {contrato.numeroContrato}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {contrato.empresa || '—'}
                        </Typography>
                      </Box>
                    </Box>
                    {contrato.setor && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Setor: {contrato.setor}
                      </Typography>
                    )}
                    {contrato.qtdNotas > 0 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5 }}
                      >
                        Nota(s) fiscal(is):{' '}
                        {contrato.notasNumeros?.length > 80
                          ? `${contrato.notasNumeros.slice(0, 80)}…`
                          : contrato.notasNumeros}
                      </Typography>
                    )}
                    <Typography variant="caption" color="primary" display="block" sx={{ mt: 1, fontWeight: 600 }}>
                      Clique para dashboard e envio por mês →
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
